import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AxiosResponse } from 'axios'
import { all_routes } from '../../../router/all_routes'
import CollapseHeader from '../../../../core/common/collapse-header'
import { checkPermission } from '../../../../core/permissons/CheckPermissons'
import apiDefinitions from '../../../../core/api/apiDefinitions'
import { showError, showDeleteConfirmation, showItemAlreadyUsed } from '../../../../core/alert/sweetAlertConfig'
import { Button } from 'react-bootstrap'
import AddItemModule from './AddItemModule'
import UpdateItemModule from './UpdateItemModule'
import ViewItemModule from './ViewItemModule'

/** DTOs */
interface ItemDTO {
  id: number
  itemName: string
  brandId: number | null
  description: string | null
  capacity: string | null
  serviceFrequency: string | null
  itemTypeId: number | null
  buildingId: number | null
  floorId: number | null
  roomId: number | null
}

interface ItemBrand {
  id: number
  brandName: string
  description: string
}

interface ItemType {
  id: number
  itemType: string
  description: string
}

interface LocationDTO {
  id: number
  name: string
  address: string
  lat: number
  lng: number
  description?: string
  createdBy: string
  createdAt: string
  updatedBy?: string
  updatedAt?: string
}

interface BuildingDTO {
  id: number
  name: string
  noOfFloors: number
  locationName: string
  locationId: number
  description?: string | null
}

interface FloorDTO {
  id: number
  name: string
  floorNumber: number
  buildingId: number
  buildingName: string
  locationName: string
  createdBy: string
  createdAt: string
  updatedBy?: string
  updatedAt?: string
}

interface RoomDTO {
  id: number
  name: string
  floorId: number
  buildingId: number
  status: string
  createdBy: string
  createdAt: string
  updatedBy?: string
  updatedAt?: string
  description?: string
}

interface GetLocationsResponse {
  locations: LocationDTO[]
  total: number
}

/** Tabs */
const navItems = [
     { label: 'Item Services', icon: 'ti ti-tools', link: all_routes.itemServiceCards, permission: 'ROLE_GET_ITEM_SERVICE_CARD' },
  { label: 'Items', icon: 'ti ti-box', link: all_routes.items, permission: 'ROLE_ADD_ITEM' },
  { label: 'Item Type & Brand', icon: 'ti ti-plus', link: all_routes.additemtypeandbrand, permission: 'ROLE_ADD_ITEM' },

]

const Item: React.FC = () => {
  const [locations, setLocations] = useState<LocationDTO[]>([])
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)

  const [buildings, setBuildings] = useState<BuildingDTO[]>([])
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null)

  const [floors, setFloors] = useState<FloorDTO[]>([])
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null)

  const [rooms, setRooms] = useState<RoomDTO[]>([])
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null)

  const [items, setItems] = useState<ItemDTO[]>([])
  const [itemBrands, setItemBrands] = useState<ItemBrand[]>([])
  const [itemTypes, setItemTypes] = useState<ItemType[]>([])

  const [selectedItem, setSelectedItem] = useState<ItemDTO | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Pagination */
  const [itemPageNo, setItemPageNo] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const pageSize = 10 // server-side page size (used only for UI disabling if total is returned)

  /** Search */
  const [searchTerm, setSearchTerm] = useState('')          // what user types
  const [appliedSearch, setAppliedSearch] = useState('')    // what we send to the API

  /** Modals */
  const [showCreateItem, setShowCreateItem] = useState(false)
  const [showUpdateItem, setShowUpdateItem] = useState(false)
  const [showViewItem, setShowViewItem] = useState(false)

  /** Hydrated caches (for parent names outside current filters) */
  const [hydratedBuildings, setHydratedBuildings] = useState<Map<number, BuildingDTO>>(new Map())
  const [hydratedFloors, setHydratedFloors] = useState<Map<number, FloorDTO>>(new Map())
  const [hydratedRooms, setHydratedRooms] = useState<Map<number, RoomDTO>>(new Map())

  const navigate = useNavigate()

  /** Auth check */
  const checkToken = () => {
    const token = localStorage.getItem('token')
    if (!token) {
      showError('Authentication token not found. Please log in.')
      navigate(all_routes.login)
      return false
    }
    return token
  }

  /** Fetch Locations */
  const fetchLocations = async () => {
    if (!checkToken()) return
    setIsLoading(true); setError(null)
    try {
      const payload = { filterType: '', filterValue: '', date: '' }
      const response: AxiosResponse = await apiDefinitions.getLocations('1', payload)
      if (response.status === 200) {
        const data: GetLocationsResponse = response.data.data
        setLocations(data.locations || [])
        if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.')
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch locations')
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else {
        showError('Failed to fetch locations. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** ID maps */
  const locationsById = useMemo(() => {
    const m = new Map<number, LocationDTO>()
    locations.forEach(l => m.set(l.id, l))
    return m
  }, [locations])

  const buildingsById = useMemo(() => {
    const m = new Map<number, BuildingDTO>()
    buildings.forEach(b => m.set(b.id, b))
    return m
  }, [buildings])

  const floorsById = useMemo(() => {
    const m = new Map<number, FloorDTO>()
    floors.forEach(f => m.set(f.id, f))
    return m
  }, [floors])

  const roomsById = useMemo(() => {
    const m = new Map<number, RoomDTO>()
    rooms.forEach(r => m.set(r.id, r))
    return m
  }, [rooms])

  /** Fetch Buildings for a Location */
  const fetchBuildings = async () => {
    if (!checkToken() || !selectedLocationId) return
    if (!checkPermission('ROLE_GET_LOCATIONS')) {
      showError('You do not have permission to view buildings.')
      return
    }
    setIsLoading(true); setError(null)
    try {
      const response: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(selectedLocationId)
      if (response.status === 200) {
        setBuildings(response.data.data || [])
        setSelectedBuildingId(null)
        setFloors([]); setSelectedFloorId(null)
        setRooms([]); setSelectedRoomId(null)
        if ((response.data.data || []).length === 0) setError('No buildings found for the selected location.')
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch buildings')
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else if (err.response?.status === 404) {
        setError('No buildings found for this location')
      } else {
        showError('Failed to fetch buildings. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Fetch Floors for a Building */
  const fetchFloors = async () => {
    if (!checkToken() || !selectedBuildingId) return
    if (!checkPermission('ROLE_VIEW_FLOOR')) {
      showError('You do not have permission to view floors.')
      return
    }
    setIsLoading(true); setError(null)
    try {
      const response: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId)
      if (response.status === 200) {
        setFloors(response.data.data || [])
        setSelectedFloorId(null)
        setRooms([]); setSelectedRoomId(null)
        if ((response.data.data || []).length === 0) setError('No floors found for the selected building.')
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch floors')
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else if (err.response?.status === 404) {
        setError('No floors found for this building')
      } else {
        showError('Failed to fetch floors. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Fetch Rooms for a Floor */
  const fetchRooms = async () => {
    if (!checkToken() || !selectedFloorId) return
    if (!checkPermission('ROLE_GET_ROOMS')) {
      showError('You do not have permission to view rooms.')
      return
    }
    setIsLoading(true); setError(null)
    try {
      const response: AxiosResponse = await apiDefinitions.getRoomsByFloorId(selectedFloorId)
      if (response.status === 200) {
        setRooms(response.data.data || [])
        setSelectedRoomId(null)
        if ((response.data.data || []).length === 0) setError('No rooms found for this floor.')
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch rooms')
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else if (err.response?.status === 404) {
        setError('No rooms found for this floor')
      } else {
        showError('Failed to fetch rooms. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Fetch Item Brands */
  const fetchItemBrands = async () => {
    if (!checkToken()) return
    setIsLoading(true)
    try {
      const response = await apiDefinitions.getAllItemBrands(1)
      if (response.data.status === 'S1000') {
        setItemBrands(response.data.data || [])
      } else {
        showError(response.data.statusDescription || 'Failed to fetch item brands')
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else {
        showError(error.response?.data?.statusDescription || 'Error fetching item brands')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Fetch Item Types */
  const fetchItemTypes = async () => {
    if (!checkToken()) return
    setIsLoading(true)
    try {
      const response = await apiDefinitions.getAllItemTypes(1)
      if (response.data.status === 'S1000') {
        setItemTypes(response.data.data || [])
      } else {
        showError(response.data.statusDescription || 'Failed to fetch item types')
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else {
        showError(error.response?.data?.statusDescription || 'Error fetching item types')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Fetch Items (PAGINATED) */
  const fetchItems = async () => {
    if (!checkToken()) return
    setIsLoading(true); setError(null)
    try {
      const payload: any = {}

      // filters: room > floor > building
      if (selectedRoomId) {
        payload.filterType = 'roomId'
        payload.filterValue = String(selectedRoomId)
      } else if (selectedFloorId) {
        payload.filterType = 'floorId'
        payload.filterValue = String(selectedFloorId)
      } else if (selectedBuildingId) {
        payload.filterType = 'buildingId'
        payload.filterValue = String(selectedBuildingId)
      }

      // server-side search if applied
      if (appliedSearch.trim().length > 0) {
        payload.search = appliedSearch.trim()           // <-- send a "search" field
        // if your backend expects (filterType:'search', filterValue:'...'), use these instead:
        // payload.filterType = 'search'
        // payload.filterValue = appliedSearch.trim()
      }

      // 🔹 NEW: pass page number into API call
      const response = await apiDefinitions.getAllItems(itemPageNo, payload)

      if (response.data.status === 'S1000') {
        setItems(response.data.data || [])
        setTotalItems(response.data.total || 0)
        if ((response.data.data || []).length === 0) setError('No items found for the selected filters.')
      } else {
        showError(response.data.statusDescription || 'Failed to fetch items')
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else if (error.response?.status === 404) {
        setError('No items found for the selected filters')
      } else {
        showError(error.response?.data?.statusDescription || 'Error fetching items')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Delete Item */
  const handleDeleteItem = (id: number) => {
    if (!checkToken()) return
    if (!checkPermission('ROLE_ADD_ITEM')) {
      showError('You do not have permission to delete items.')
      return
    }
    showDeleteConfirmation(async () => {
      try {
        setIsLoading(true)
        const response = await apiDefinitions.deleteItem(id)
        if (response.data.status === 'S1000') {
          // After deletion, reload from current page. If page becomes empty, go back one page.
          await fetchItems()
          if (items.length === 1 && itemPageNo > 1) {
            setItemPageNo(p => p - 1)
          }
        } else {
          showError(response.data.statusDescription || 'Failed to delete item')
        }
      } catch (error: any) {
        if (error.response?.status === 409) {
          showItemAlreadyUsed("Item is associated with existing service records and cannot be deleted.");
        } else if (error.response?.status === 401) {
          showError('Unauthorized access. Your session may have expired. Please log in again.')
          localStorage.removeItem('token'); navigate(all_routes.login)
        } else {
          showError(error.response?.data?.statusDescription || 'Error deleting item')
        }
      } finally {
        setIsLoading(false)
      }
    })
  }

  /** View Item */
  const handleViewItem = async (id: number) => {
    if (!checkToken()) return
    try {
      setIsLoading(true)
      const response = await apiDefinitions.getItemById(id)
      if (response.data.status === 'S1000') {
        setSelectedItem(response.data.data as ItemDTO)
        setShowViewItem(true)
      } else {
        showError(response.data.statusDescription || 'Failed to fetch item')
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        showError('Unauthorized access. Your session may have expired. Please log in again.')
        localStorage.removeItem('token'); navigate(all_routes.login)
      } else {
        showError(error.response?.data?.statusDescription || 'Error fetching item')
      }
    } finally {
      setIsLoading(false)
    }
  }

  /** Pagination */
  const handleItemPrevious = () => {
    if (itemPageNo > 1) setItemPageNo(itemPageNo - 1)
  }
  const handleItemNext = () => {
    // If server returns total, disable when last page reached
    if (itemPageNo * pageSize < totalItems) setItemPageNo(itemPageNo + 1)
  }

  /** Search handlers */
  const applySearch = () => {
    setAppliedSearch(searchTerm.trim())
    setItemPageNo(1) // reset to first page on new search
  }
  const clearSearch = () => {
    setSearchTerm('')
    setAppliedSearch('')
    setItemPageNo(1)
  }

  /** Initial load */
  useEffect(() => {
    fetchLocations()
    fetchItemBrands()
    fetchItemTypes()
  }, [])

  /** React to selections (and reset pagination) */
  useEffect(() => {
    if (selectedLocationId) {
      fetchBuildings()
      setItemPageNo(1)
    } else {
      // cleared location → clear children & reload items
      setBuildings([]); setSelectedBuildingId(null)
      setFloors([]); setSelectedFloorId(null)
      setRooms([]); setSelectedRoomId(null)
      setItemPageNo(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId])

  useEffect(() => {
    if (selectedBuildingId) {
      fetchFloors()
      setItemPageNo(1)
    } else {
      setFloors([]); setSelectedFloorId(null)
      setRooms([]); setSelectedRoomId(null)
      setItemPageNo(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId])

  useEffect(() => {
    if (selectedFloorId) {
      fetchRooms()
      setItemPageNo(1)
    } else {
      setRooms([]); setSelectedRoomId(null)
      setItemPageNo(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloorId])

  /** Load items when filters/page/search change */
  useEffect(() => {
    fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, selectedBuildingId, selectedFloorId, selectedRoomId, itemPageNo, appliedSearch])

  /** Lazy hydration for missing parents (rooms → floors → buildings) */
  useEffect(() => {
    if (!items?.length) return

    const needBuildings = new Set<number>()
    const needFloors = new Set<number>()
    const needRooms = new Set<number>()

    for (const it of items) {
      if (it.buildingId && !buildingsById.get(it.buildingId) && !hydratedBuildings.get(it.buildingId)) {
        needBuildings.add(it.buildingId)
      }
      if (it.floorId && !floorsById.get(it.floorId) && !hydratedFloors.get(it.floorId)) {
        needFloors.add(it.floorId)
      }
      if (it.roomId && !roomsById.get(it.roomId) && !hydratedRooms.get(it.roomId)) {
        needRooms.add(it.roomId)
      }
    }

    const run = async () => {
      const nextRooms = new Map(hydratedRooms)
      const nextFloors = new Map(hydratedFloors)
      const nextBuildings = new Map(hydratedBuildings)

      // 1. Fetch missing Rooms
      if (needRooms.size) {
        const results = await Promise.allSettled(
          Array.from(needRooms).map(id => apiDefinitions.getRoomById(id))
        )
        results.forEach(r => {
          const data = (r.status === 'fulfilled' ? r.value?.data?.data : null) as RoomDTO | null
          if (data?.id) nextRooms.set(data.id, data)
        })
        setHydratedRooms(new Map(nextRooms))
      }

      // 2. Collect missing Floors: from direct item floorId + rooms' floorId
      const alsoNeedFloors = new Set<number>(needFloors)
      nextRooms.forEach(r => {
        if (r.floorId && !floorsById.get(r.floorId) && !nextFloors.get(r.floorId)) {
          alsoNeedFloors.add(r.floorId)
        }
      })

      // Fetch missing Floors
      if (alsoNeedFloors.size) {
        const results = await Promise.allSettled(
          Array.from(alsoNeedFloors).map(id => apiDefinitions.getFloorById(id))
        )
        results.forEach(r => {
          const data = (r.status === 'fulfilled' ? r.value?.data?.data : null) as FloorDTO | null
          if (data?.id) nextFloors.set(data.id, data)
        })
        setHydratedFloors(new Map(nextFloors))
      }

      // 3. Collect missing Buildings: from direct item buildingId + floors' buildingId + rooms' buildingId
      const alsoNeedBuildings = new Set<number>(needBuildings)
      nextFloors.forEach(f => {
        if (f.buildingId && !buildingsById.get(f.buildingId) && !nextBuildings.get(f.buildingId)) {
          alsoNeedBuildings.add(f.buildingId)
        }
      })
      nextRooms.forEach(r => {
        if (r.buildingId && !buildingsById.get(r.buildingId) && !nextBuildings.get(r.buildingId)) {
          alsoNeedBuildings.add(r.buildingId)
        }
      })

      // Fetch missing Buildings
      if (alsoNeedBuildings.size) {
        const results = await Promise.allSettled(
          Array.from(alsoNeedBuildings).map(id => apiDefinitions.getBuildingById(id))
        )
        results.forEach(r => {
          const data = (r.status === 'fulfilled' ? r.value?.data?.data : null) as BuildingDTO | null
          if (data?.id) nextBuildings.set(data.id, data)
        })
        setHydratedBuildings(new Map(nextBuildings))
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, buildingsById, floorsById, roomsById])

  /** Resolvers */
  const resolveBuilding = (it: ItemDTO): BuildingDTO | undefined => {
    if (it.buildingId) {
      const b = buildingsById.get(it.buildingId) || hydratedBuildings.get(it.buildingId)
      if (b) return b
    }
    if (it.floorId) {
      const f = floorsById.get(it.floorId) || hydratedFloors.get(it.floorId)
      if (f?.buildingId) {
        const b = buildingsById.get(f.buildingId) || hydratedBuildings.get(f.buildingId)
        if (b) return b
      }
    }
    if (it.roomId) {
      const r = roomsById.get(it.roomId) || hydratedRooms.get(it.roomId)
      if (r) {
        if (r.buildingId) {
          const b = buildingsById.get(r.buildingId) || hydratedBuildings.get(r.buildingId)
          if (b) return b
        }
        if (r.floorId) {
          const f = floorsById.get(r.floorId) || hydratedFloors.get(r.floorId)
          if (f?.buildingId) {
            const b = buildingsById.get(f.buildingId) || hydratedBuildings.get(f.buildingId)
            if (b) return b
          }
        }
      }
    }
    return undefined
  }

  const resolveFloorName = (it: ItemDTO): string => {
    if (it.floorId) {
      const f = floorsById.get(it.floorId) || hydratedFloors.get(it.floorId)
      if (f?.name) return f.name
    }
    if (it.roomId) {
      const r = roomsById.get(it.roomId) || hydratedRooms.get(it.roomId)
      if (r?.floorId) {
        const f = floorsById.get(r.floorId) || hydratedFloors.get(r.floorId)
        if (f?.name) return f.name
      }
    }
    return 'N/A'
  }

  const resolveRoomName = (it: ItemDTO): string => {
    if (it.roomId) {
      const r = roomsById.get(it.roomId) || hydratedRooms.get(it.roomId)
      if (r?.name) return r.name
    }
    return 'N/A'
  }

  const resolveLocationName = (it: ItemDTO): string => {
    const b = resolveBuilding(it)
    if (b?.locationId) {
      const loc = locationsById.get(b.locationId)
      if (loc?.name) return loc.name
    }
    return 'N/A'
  }

  const resolveBuildingName = (it: ItemDTO): string => {
    if (it.floorId) {
      const f = floorsById.get(it.floorId) || hydratedFloors.get(it.floorId)
      if (f?.buildingName) return f.buildingName
    }
    if (it.roomId) {
      const r = roomsById.get(it.roomId) || hydratedRooms.get(it.roomId)
      if (r?.floorId) {
        const f = floorsById.get(r.floorId) || hydratedFloors.get(r.floorId)
        if (f?.buildingName) return f.buildingName
      }
    }
    const b = resolveBuilding(it)
    return b?.name ?? 'N/A'
  }

  /** Breadcrumb */
  const breadcrumb: string[] = []
  if (selectedLocationId) breadcrumb.push(locations.find(l => l.id === selectedLocationId)?.name || '')
  if (selectedBuildingId) breadcrumb.push(buildings.find(b => b.id === selectedBuildingId)?.name || '')
  if (selectedFloorId) breadcrumb.push(floors.find(f => f.id === selectedFloorId)?.name || '')
  if (selectedRoomId) breadcrumb.push(rooms.find(r => r.id === selectedRoomId)?.name || '')

  return (
    <div className="page-wrapper">
      <style>
        {`
          .item-table th, .item-table td {
            vertical-align: middle;
            padding: 0.75rem;
            font-size: 0.85rem;
          }
          .item-table th {
            background: #f8f9fa;
            color: #333;
            font-weight: 600;
          }
          .item-table tr:hover {
            background: #f1f3f5;
          }
          .item-table .actions {
            display: flex;
            gap: 0.5rem;
            justify-content: center;
          }
        `}
      </style>

      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Facility Maintenance/Item Service/Item</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons"><CollapseHeader /></div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, index) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={index}>
                        <Link to={item.link} className={`nav-link px-0 ${index === 1 ? 'active' : ''}`}>
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div
                  className="breadcrumb d-flex"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#666',
                    fontSize: '0.9rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {breadcrumb.length === 0 ? (
                      <span>No location selected</span>
                    ) : (
                      breadcrumb.map((item, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: '#f8f9fa',
                            borderRadius: '4px',
                          }}
                        >
                          {item}
                        </span>
                      ))
                    )}
                  </div>

                  <Button
                    variant="success"
                    onClick={() => setShowCreateItem(true)}
                    disabled={!checkPermission('ROLE_ADD_ITEM')}
                  >
                    🛠 New Item
                  </Button>
                </div>

                {/* Toolbar: SEARCH */}
                <div className="toolbar" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
                  <input
                    className="form-control"
                    style={{ maxWidth: 360 }}
                    placeholder="Search items…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') applySearch() }}
                  />
                  <Button variant="primary" onClick={applySearch}>Search</Button>
                  {appliedSearch && (
                    <Button variant="outline-secondary" onClick={clearSearch}>Clear</Button>
                  )}
                </div>

                <div className="main-container" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {/* Sidebar filters */}
                  <div
                    className="left-sidebar"
                    style={{
                      flex: '0 0 200px',
                      background: 'rgba(255,255,255,0.95)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      overflowY: 'auto',
                    }}
                  >
                    <div className="section" style={{ marginBottom: '1rem' }}>
                      <h3 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Locations</h3>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <select
                          className="form-select"
                          value={selectedLocationId || ''}
                          onChange={(e) => setSelectedLocationId(Number(e.target.value) || null)}
                          disabled={locations.length === 0}
                          style={{ flex: 1 }}
                        >
                          <option value="">Select a location</option>
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="section" style={{ marginBottom: '1rem' }}>
                      <h3 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Buildings</h3>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <select
                          className="form-select"
                          value={selectedBuildingId || ''}
                          onChange={(e) => setSelectedBuildingId(Number(e.target.value) || null)}
                          disabled={buildings.length === 0 || !selectedLocationId}
                          style={{ flex: 1 }}
                        >
                          <option value="">Select a building</option>
                          {buildings.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="section" style={{ marginBottom: '1rem' }}>
                      <h3 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Floors</h3>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <select
                          className="form-select"
                          value={selectedFloorId || ''}
                          onChange={(e) => setSelectedFloorId(Number(e.target.value) || null)}
                          disabled={floors.length === 0 || !selectedBuildingId}
                          style={{ flex: 1 }}
                        >
                          <option value="">Select a floor</option>
                          {floors.map((f) => (
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="section">
                      <h3 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Rooms</h3>
                      <select
                        className="form-select"
                        value={selectedRoomId || ''}
                        onChange={(e) => setSelectedRoomId(Number(e.target.value) || null)}
                        disabled={rooms.length === 0 || !selectedFloorId}
                      >
                        <option value="">Select a room</option>
                        {rooms.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  <div
                    className="item-table-container"
                    style={{ flex: 1, background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}
                  >
                    {isLoading ? (
                      <div className="text-center my-3">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : error ? (
                      <div className="alert alert-danger" role="alert">{error}</div>
                    ) : items.length === 0 ? (
                      <div className="alert alert-info" role="alert">No items found for the selected filters.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table item-table">
                          <thead>
                            <tr>
                              <th>Item Name</th>
                              <th>Brand</th>
                              <th>Type</th>
                              <th>Building</th>
                              <th>Floor</th>
                              <th>Room</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item) => (
                              <tr key={item.id}>
                                <td>{item.itemName}</td>
                                <td>{item.brandId ? (itemBrands.find((b) => b.id === item.brandId)?.brandName || 'N/A') : 'N/A'}</td>
                                <td>{item.itemTypeId ? (itemTypes.find((t) => t.id === item.itemTypeId)?.itemType || 'N/A') : 'N/A'}</td>
                                <td>{resolveBuildingName(item)}</td>
                                <td>{resolveFloorName(item)}</td>
                                <td>{resolveRoomName(item)}</td>
                                <td className="actions">
                                  <Button
                                    variant="info"
                                    onClick={() => handleViewItem(item.id)}
                                    disabled={isLoading}
                                    title="View"
                                  >
                                    <i className="ti ti-eye" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    onClick={() => { setSelectedItem(item); setShowUpdateItem(true) }}
                                    disabled={isLoading || !checkPermission('ROLE_ADD_ITEM')}
                                    title="Edit"
                                  >
                                    <i className="ti ti-pencil" />
                                  </Button>
                                  <Button
                                    variant="danger"
                                    onClick={() => handleDeleteItem(item.id)}
                                    disabled={isLoading || !checkPermission('ROLE_ADD_ITEM')}
                                    title="Delete"
                                  >
                                    <i className="ti ti-trash" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="card-footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem', background: '#f8f9fa' }}>
                      <Button
                        variant="secondary"
                        onClick={handleItemPrevious}
                        disabled={itemPageNo === 1 || isLoading}
                      >
                        Previous
                      </Button>
                      <span>Page {itemPageNo}{appliedSearch ? ` — results for "${appliedSearch}"` : ''}</span>
                      <Button
                        variant="secondary"
                        onClick={handleItemNext}
                        disabled={itemPageNo * pageSize >= totalItems || isLoading}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Modals */}
        {showCreateItem && (
          <AddItemModule
            show={showCreateItem}
            onHide={() => setShowCreateItem(false)}
            onSuccess={() => { setItemPageNo(1); fetchItems() }}
            locations={locations}
            buildings={buildings}
            floors={floors}
            rooms={rooms}
            itemBrands={itemBrands}
            itemTypes={itemTypes}
          />
        )}

        {showUpdateItem && selectedItem && (
          <UpdateItemModule
            show={showUpdateItem}
            onHide={() => setShowUpdateItem(false)}
            item={selectedItem as any}
            onSuccess={() => fetchItems()}
            locations={locations}
            buildings={buildings}
            floors={floors}
            rooms={rooms}
            itemBrands={itemBrands}
            itemTypes={itemTypes}
          />
        )}

        {showViewItem && selectedItem && (
          <ViewItemModule
            show={showViewItem}
            onHide={() => setShowViewItem(false)}
            item={selectedItem as any}
            itemBrands={itemBrands}
            itemTypes={itemTypes}
            buildings={buildings}
            floors={floors}
            rooms={rooms}
          />
        )}
      </div>
    </div>
  )
}

export default Item
